import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Users2, Zap, MessagesSquare } from "lucide-react";
import InstallAppButton from "@/components/InstallAppButton";
import AnimatedNeurons from "@/components/AnimatedNeurons";

/* ------------------------------------------------------------------ */
/*  Login — due neuroni si toccano al centro, dalla sinapsi nasce la card */
/* ------------------------------------------------------------------ */

const KEYFRAMES = `
@keyframes syn-neurons-out {
  0%   { opacity: 1; filter: blur(0) brightness(1); }
  100% { opacity: 0; filter: blur(10px) brightness(0.4); }
}
@keyframes syn-center-pulse {
  0%, 100% { opacity: 0.55; transform: translate(-50%,-50%) scale(1); }
  50%      { opacity: 1;    transform: translate(-50%,-50%) scale(1.25); }
}
@keyframes syn-flash {
  0%   { opacity: 0; transform: translate(-50%,-50%) scale(0.2); }
  35%  { opacity: 1; transform: translate(-50%,-50%) scale(1.6); }
  100% { opacity: 0; transform: translate(-50%,-50%) scale(4);   }
}
@keyframes syn-spark-fly {
  0%   { opacity: 0; transform: translate(var(--fx), var(--fy)) scale(0.3); }
  10%  { opacity: 1; }
  90%  { opacity: 1; }
  100% { opacity: 0; transform: translate(0,0) scale(1.4); }
}
@keyframes syn-card-birth {
  0%   { opacity: 0; transform: scale(0.02); filter: blur(14px) brightness(2.4); }
  35%  { opacity: 1; transform: scale(0.35); filter: blur(8px)  brightness(1.6); }
  70%  { opacity: 1; transform: scale(1.04); filter: blur(2px)  brightness(1.15); }
  100% { opacity: 1; transform: scale(1);    filter: blur(0)    brightness(1);    }
}
@keyframes syn-fade-up {
  0%   { opacity: 0; transform: translateY(14px); }
  100% { opacity: 1; transform: translateY(0);    }
}
@media (prefers-reduced-motion: reduce) {
  .syn-anim { animation: none !important; opacity: 1 !important; transform: none !important; filter: none !important; }
}
`;

// Scintille che convergono al centro da varie direzioni (in px, offset dal centro)
const SPARKS = [
  { fx: -520, fy: -220, delay: 2.8, dur: 1.6, color: "#FBBF24" },
  { fx: -560, fy:   40, delay: 2.6, dur: 1.7, color: "#F97316" },
  { fx: -480, fy:  220, delay: 3.0, dur: 1.5, color: "#FDE68A" },
  { fx: -300, fy: -280, delay: 3.2, dur: 1.4, color: "#FBBF24" },
  { fx:  540, fy: -240, delay: 2.7, dur: 1.6, color: "#22D3EE" },
  { fx:  580, fy:   30, delay: 2.9, dur: 1.6, color: "#06B6D4" },
  { fx:  500, fy:  230, delay: 3.1, dur: 1.5, color: "#67E8F9" },
  { fx:  320, fy: -270, delay: 3.3, dur: 1.4, color: "#22D3EE" },
];

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Rimuove il backdrop dei neuroni dal DOM dopo il fade-out per pulire la pagina
  const [showBackdrop, setShowBackdrop] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setShowBackdrop(false), 6300);
    return () => clearTimeout(t);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Accesso effettuato");
      navigate("/dashboard");
    } else {
      setError(res.error || "Credenziali non valide");
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#020615]" data-testid="login-page">
      <style>{KEYFRAMES}</style>

      {/* Sfondo cinematografico: due neuroni si disegnano da soli come rami organici */}
      {showBackdrop && (
        <div className="absolute inset-0 pointer-events-none">
          {/* SVG procedurale — non è una foto: dendriti che crescono via stroke-dashoffset */}
          <div className="absolute inset-0 syn-anim"
               style={{
                 animation: "syn-neurons-out 1.4s ease-in 4.2s forwards",
               }}>
            <AnimatedNeurons startDelay={0.9} />
            {/* Vignette per fondere i bordi con lo sfondo scuro */}
            <div className="absolute inset-0"
                 style={{
                   background:
                     "radial-gradient(70% 60% at 50% 50%, rgba(0,0,0,0) 0%, rgba(2,6,21,0.35) 55%, rgba(2,6,21,0.85) 90%, #020615 100%)",
                 }} />
          </div>

          {/* Pulse morbido sul punto sinaptico centrale */}
          <div className="absolute left-1/2 top-1/2 syn-anim"
               style={{
                 width: 240, height: 240,
                 transform: "translate(-50%,-50%)",
                 background:
                   "radial-gradient(circle, rgba(255,255,255,0.85) 0%, rgba(251,191,36,0.55) 25%, rgba(52,211,153,0.25) 55%, transparent 80%)",
                 filter: "blur(6px)",
                 opacity: 0,
                 animation: "syn-center-pulse 1.8s ease-in-out 2.5s 1 both",
               }} />

          {/* Scintille che volano lungo i dendriti verso il centro */}
          <div className="absolute left-1/2 top-1/2" style={{ transform: "translate(-50%,-50%)" }}>
            {SPARKS.map((s, i) => (
              <div key={i} className="absolute syn-anim"
                   style={{
                     "--fx": `${s.fx}px`,
                     "--fy": `${s.fy}px`,
                     width: 8, height: 8, borderRadius: "50%",
                     background: s.color,
                     boxShadow: `0 0 12px ${s.color}, 0 0 24px ${s.color}`,
                     left: -4, top: -4,
                     opacity: 0,
                     animation: `syn-spark-fly ${s.dur}s cubic-bezier(0.5,0,0.75,0) ${s.delay}s both`,
                   }} />
            ))}
          </div>

          {/* LAMPO al centro: qui nasce la card */}
          <div className="absolute left-1/2 top-1/2 syn-anim"
               style={{
                 width: 360, height: 360,
                 background:
                   "radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(253,224,71,0.9) 15%, rgba(34,211,238,0.4) 45%, transparent 70%)",
                 filter: "blur(4px)",
                 borderRadius: "50%",
                 opacity: 0,
                 animation: "syn-flash 1.1s ease-out 3.95s both",
               }} />
        </div>
      )}

      {/* Gradient di base che resta sotto la card dopo il fade */}
      <div className="absolute inset-0 pointer-events-none"
           style={{
             background:
               "radial-gradient(60% 55% at 50% 50%, rgba(30,58,138,0.25) 0%, rgba(5,13,46,0.55) 55%, #020615 100%)",
             opacity: 0.9,
           }} />

      {/* Grain overlay */}
      <div className="absolute inset-0 pointer-events-none opacity-60"
           style={{
             backgroundImage: "radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)",
             backgroundSize: "3px 3px",
           }} />

      <div className="relative z-10 flex min-h-screen items-center justify-center p-4 md:p-8">
        <div className="w-full max-w-5xl syn-anim"
             style={{
               animation: "syn-card-birth 1.5s cubic-bezier(0.22,1,0.36,1) 4.05s both",
               transformOrigin: "50% 50%",
             }}>
          <div className="relative rounded-3xl overflow-hidden border border-white/10
                          bg-white/[0.04] backdrop-blur-xl shadow-2xl shadow-emerald-500/10
                          ring-1 ring-emerald-400/10"
               data-testid="login-card">
            <div className="grid md:grid-cols-[1.15fr_1fr]">
              {/* Colonna brand */}
              <div className="relative p-8 md:p-12 flex flex-col">
                <div className="flex items-center gap-3 syn-anim"
                     style={{ animation: "syn-fade-up 0.7s ease-out 5.6s both" }}>
                  <img src="/icon.svg?v=20" alt="" className="h-11 w-11 rounded-xl shadow-lg shadow-emerald-500/10" />
                  <div className="leading-tight">
                    <div className="font-heading text-2xl font-extrabold text-white tracking-tight">
                      Synappsi
                    </div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-300/90">
                      Health · Connected
                    </div>
                  </div>
                </div>

                <h1 className="mt-10 font-heading text-4xl md:text-5xl font-extrabold text-white leading-[1.05] tracking-tight syn-anim"
                    style={{ animation: "syn-fade-up 0.8s ease-out 5.8s both" }}>
                  Connessione<br/>
                  <span className="bg-gradient-to-r from-sky-300 via-emerald-300 to-emerald-400 bg-clip-text text-transparent">
                    intelligente
                  </span><br/>
                  tra professionisti<br/>della salute.
                </h1>

                <p className="mt-5 text-sm md:text-[15px] text-slate-300/90 leading-relaxed max-w-md syn-anim"
                   style={{ animation: "syn-fade-up 0.8s ease-out 6.0s both" }}>
                  Un unico canale sicuro per collegare laboratori, medici di base, diagnostica e specialisti.
                  Valori d&apos;allarme, richieste esami, referti — tutto tracciato, GDPR-compliant.
                </p>

                <ul className="mt-8 space-y-3 max-w-md">
                  {[
                    { icon: Users2, title: "Specialisti e Medici di base, in dialogo diretto",
                      desc: "Un canale sicuro per consulti, prescrizioni e follow-up tra colleghi.", delay: 6.15 },
                    { icon: Zap, title: "Valori d'allarme in tempo reale",
                      desc: "Il laboratorio raggiunge il medico di base in un tocco.", delay: 6.30 },
                    { icon: MessagesSquare, title: "Richieste strutturate",
                      desc: "Ripetizioni esami, aggiunte diagnostiche, chiarimenti — sempre tracciati.", delay: 6.45 },
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-3 syn-anim"
                        style={{ animation: `syn-fade-up 0.7s ease-out ${f.delay}s both` }}>
                      <div className="h-9 w-9 rounded-lg flex-shrink-0 flex items-center justify-center
                                      bg-gradient-to-br from-[#1E3A8A] to-[#10B981] shadow-md shadow-emerald-500/20">
                        <f.icon className="h-4 w-4 text-white" strokeWidth={2.2} />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-white leading-tight">{f.title}</div>
                        <div className="text-xs text-slate-300/80 leading-snug mt-0.5">{f.desc}</div>
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto pt-10 syn-anim"
                     style={{ animation: "syn-fade-up 0.7s ease-out 6.7s both" }}>
                  <InstallAppButton variant="ghost" />
                </div>
              </div>

              {/* Colonna form */}
              <div className="relative p-8 md:p-12 md:pl-10 border-t md:border-t-0 md:border-l border-white/10
                              bg-white/[0.06] backdrop-blur">
                <div className="max-w-sm mx-auto md:mx-0 syn-anim"
                     style={{ animation: "syn-fade-up 0.8s ease-out 5.7s both" }}>
                  <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-white">Accedi</h2>
                  <p className="text-sm text-slate-300/80 mt-1">
                    Entra nel tuo spazio professionale.
                  </p>

                  <form onSubmit={submit} className="mt-7 space-y-4">
                    <div>
                      <Label htmlFor="email" className="text-slate-200 text-xs font-semibold">Email</Label>
                      <Input id="email" type="email" value={email}
                             onChange={(e) => setEmail(e.target.value)}
                             placeholder="nome@laboratorio.it"
                             data-testid="login-email" required
                             className="mt-1 h-11 bg-white/[0.08] border-white/15 text-white placeholder:text-slate-400
                                        focus:border-emerald-400 focus:ring-emerald-400/30" />
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="password" className="text-slate-200 text-xs font-semibold">Password</Label>
                        <Link to="/forgot-password"
                              data-testid="link-forgot-password"
                              className="text-[11px] font-semibold text-emerald-300/90 hover:text-emerald-200 hover:underline underline-offset-4">
                          Password dimenticata?
                        </Link>
                      </div>
                      <Input id="password" type="password" value={password}
                             onChange={(e) => setPassword(e.target.value)}
                             placeholder="••••••••"
                             data-testid="login-password" required
                             className="mt-1 h-11 bg-white/[0.08] border-white/15 text-white placeholder:text-slate-400
                                        focus:border-emerald-400 focus:ring-emerald-400/30" />
                    </div>

                    {error && (
                      <div className="text-sm text-red-100 bg-red-500/20 border border-red-400/30 rounded-lg p-3"
                           data-testid="login-error">{error}</div>
                    )}

                    <Button type="submit" disabled={loading} data-testid="login-submit"
                            className="w-full h-12 rounded-xl text-base font-semibold
                                       bg-gradient-to-r from-[#1E3A8A] via-[#2563EB] to-[#10B981]
                                       hover:from-[#1E40AF] hover:via-[#3B82F6] hover:to-[#059669]
                                       text-white shadow-lg shadow-emerald-500/25 transition-all
                                       disabled:opacity-60">
                      {loading ? "Accesso…" : "Accedi"}
                    </Button>
                  </form>

                  <div className="mt-6 text-sm text-slate-300/80 text-center">
                    Non hai un account?{" "}
                    <Link to="/register" className="text-emerald-300 font-semibold hover:text-emerald-200 hover:underline"
                          data-testid="link-register">
                      Registrati
                    </Link>
                  </div>
                  <div className="mt-4 text-center">
                    <Link to="/privacy" className="text-xs text-slate-400 hover:text-emerald-300"
                          data-testid="link-privacy">
                      Informativa privacy (GDPR)
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p className="mt-4 text-center text-[11px] text-slate-500 syn-anim"
             style={{ animation: "syn-fade-up 0.7s ease-out 6.9s both" }}>
            © {new Date().getFullYear()} Synappsi — Connessione intelligente tra professionisti della salute.
          </p>
        </div>
      </div>
    </div>
  );
}
